/** Prefix for filtering by all cities defined for a supported country (ISO2). */
export const COUNTRY_SENTINEL_PREFIX = 'country:' as const

export type RegionDef = {
  countryName: string
  iso2: string
  /** Cities shown for this region; order is preserved in pickers when those cities exist. */
  citiesInOrder: readonly string[]
}

/** Supported markets: order is used for accordion and country-level filtering. */
export const REGION_DEFINITIONS: readonly RegionDef[] = [
  { countryName: 'Finland', iso2: 'FI', citiesInOrder: ['Helsinki', 'Tampere', 'Turku'] },
  {
    countryName: 'Germany',
    iso2: 'DE',
    citiesInOrder: ['Hamburg', 'Leipzig', 'Cologne'],
  },
  {
    countryName: 'Netherlands',
    iso2: 'NL',
    citiesInOrder: [
      'Amsterdam',
      'The Hague',
      'Rotterdam',
      'Utrecht',
      'Eindhoven',
      'Haarlem',
    ],
  },
  { countryName: 'Estonia', iso2: 'EE', citiesInOrder: ['Tallinn'] },
] as const

const ISO2_TO_REGION = new Map(REGION_DEFINITIONS.map((r) => [r.iso2.toUpperCase(), r]))

/** Collapse GEO / URL punctuation variants so alias + region lookups stay stable */
function normalizeCityKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\u2019/g, "'")
    .replace(/['`]/g, '')
    .replace(/[_/]+/g, ' ')
    .replace(/\s*-\s*/g, '-')
    .replace(/-+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Map normalized keys (see normalizeCityKey) to REGION_DEFINITIONS spellings used in Firebase / artists */
const MARKET_SYNONYMS: Record<string, string> = {
  'den haag': 'The Hague',
  'the hague': 'The Hague',
  's gravenhage': 'The Hague',
  denhaag: 'The Hague',
  sgravenhage: 'The Hague',
}

/**
 * Normalizes GEO / bookmark / URL tokens to REGION_DEFINITION spelling when possible so
 * pickers and query_tattoos stay aligned with artist profile `location.city`.
 */
export function normalizePlainCityToken(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed
  const nk = normalizeCityKey(trimmed)
  const fromSyn = MARKET_SYNONYMS[nk] ?? MARKET_SYNONYMS[nk.replace(/\s/g, '')]
  if (fromSyn) return fromSyn

  // Case-insensitive match against supported markets catalogue
  for (const def of REGION_DEFINITIONS) {
    for (const cityName of def.citiesInOrder) {
      if (normalizeCityKey(cityName) === nk) return cityName
    }
  }
  return trimmed
}

/** Resolved spelling that exists in the launched-city set (or null if unsupported). */
export function resolveToLaunchedCityName(raw: string, launchedCitySet: Set<string>): string | null {
  const canon = normalizePlainCityToken(raw)
  if (!canon) return null
  if (launchedCitySet.has(canon)) return canon
  for (const c of launchedCitySet) {
    if (c.toLowerCase() === canon.toLowerCase()) return c
  }
  return null
}

/** Infer ISO2 when only a supported city name is known (e.g. legacy cached location without country). */
export function inferIso2FromSupportedCity(city: string | undefined | null): string | null {
  const normalized = normalizePlainCityToken((city ?? '').trim())
  if (!normalized) return null
  for (const def of REGION_DEFINITIONS) {
    if (def.citiesInOrder.some((c) => c.toLowerCase() === normalized.toLowerCase())) return def.iso2
  }
  return null
}

/** Country display name for accordion/flags when the city is in {@link REGION_DEFINITIONS}. */
export function countryNameFromSupportedMarketCity(city: string | undefined | null): string | null {
  const normalized = normalizePlainCityToken((city ?? '').trim())
  if (!normalized) return null
  for (const def of REGION_DEFINITIONS) {
    if (def.citiesInOrder.some((c) => c.toLowerCase() === normalized.toLowerCase())) {
      return def.countryName
    }
  }
  return null
}

export function isCountrySentinel(raw: string | undefined): raw is `${typeof COUNTRY_SENTINEL_PREFIX}${string}` {
  return !!raw?.startsWith(COUNTRY_SENTINEL_PREFIX)
}

export function countrySentinelForIso(iso2: string): string {
  return `${COUNTRY_SENTINEL_PREFIX}${iso2.trim().toUpperCase()}`
}

export function parseCountrySentinel(raw: string | undefined): string | null {
  if (!raw || !isCountrySentinel(raw)) return null
  return raw.slice(COUNTRY_SENTINEL_PREFIX.length).trim().toUpperCase() || null
}

/** Cities applied to feed / tattoos API when a country sentinel is chosen. */
export function resolveSentinelToCities(sentinel: string): string[] | null {
  const iso = parseCountrySentinel(sentinel)
  if (!iso) return null
  const region = ISO2_TO_REGION.get(iso)
  return region ? [...region.citiesInOrder] : null
}

/**
 * When a city-scoped query returns empty, widen to all catalog cities in the same country.
 * Returns null if cities are unknown, span multiple countries, or already cover that country’s full set.
 */
export function nearMatchExpansionForQueryCities(
  resolvedCities: string[] | undefined,
): { cities: string[]; countryName: string } | null {
  if (!resolvedCities?.length) return null
  const normalizedResolved = resolvedCities.map((c) => normalizePlainCityToken(c))
  const matchedRegions: { countryName: string; cities: readonly string[] }[] = []
  for (const def of REGION_DEFINITIONS) {
    const touchesRegion = normalizedResolved.some((rc) =>
      def.citiesInOrder.some((city) => city.toLowerCase() === rc.toLowerCase()),
    )
    if (touchesRegion) {
      matchedRegions.push({ countryName: def.countryName, cities: def.citiesInOrder })
    }
  }
  if (matchedRegions.length !== 1) return null
  const region = matchedRegions[0]!
  const expanded = [...region.cities]
  const resSet = new Set(normalizedResolved.map((c) => c.toLowerCase()))
  const expSet = new Set(expanded.map((c) => c.toLowerCase()))
  if (resSet.size === expSet.size && [...resSet].every((x) => expSet.has(x))) {
    return null
  }
  return { cities: expanded, countryName: region.countryName }
}

/**
 * Builds query city list:
 * — all locations → undefined (no geographic filter),
 * — country sentinel → that region’s cities (intersected elsewhere if desired),
 * — otherwise single city → [city].
 */
export function resolveLocationFilterToCities(
  selected: string | undefined,
  allLocationsText: string
): string[] | undefined {
  if (!selected || selected === allLocationsText) return undefined
  if (isCountrySentinel(selected)) {
    return resolveSentinelToCities(selected) ?? []
  }
  return [normalizePlainCityToken(selected)]
}

export function getLocationFilterDisplay(
  selected: string | undefined,
  allLocationsText: string,
  /** When provided, sentinel values that boil down to exactly one launched city show that city. */
  launchedCitySet?: Set<string>,
): string {
  if (!selected || selected === allLocationsText) return allLocationsText
  if (isCountrySentinel(selected)) {
    if (launchedCitySet?.size) {
      const fromDef = resolveSentinelToCities(selected)
      if (fromDef?.length) {
        const onMarket = fromDef.filter((c) => launchedCitySet.has(c))
        if (onMarket.length === 1) return onMarket[0]!
      }
    }
    const iso = parseCountrySentinel(selected)
    const region = iso ? ISO2_TO_REGION.get(iso) : undefined
    return region?.countryName ?? selected
  }
  return selected
}

/** Country name for `CountryFlag` — sentinel → region name; city → profile country. */
export function getFlagCountryForLocationSelection(
  selected: string | undefined,
  cityCountryByCity: Record<string, string>
): string | undefined {
  if (!selected) return undefined
  if (isCountrySentinel(selected)) {
    const iso = parseCountrySentinel(selected)
    return iso ? ISO2_TO_REGION.get(iso)?.countryName : undefined
  }
  return cityCountryByCity[selected]
}

/**
 * Country emoji flag for compact pills: only when every token resolves and all map to one country.
 * Several cities across different countries → undefined (no flag).
 */
export function uniqueFlagCountryForLocationSelections(
  selectedLocations: string[],
  allLocationsText: string,
  cityCountryByCity: Record<string, string>
): string | undefined {
  const tokens = selectedLocations.filter((s) => s && s !== allLocationsText)
  if (tokens.length === 0) return undefined

  const countries = new Set<string>()
  for (const raw of tokens) {
    let name: string | undefined
    if (isCountrySentinel(raw)) {
      const iso = parseCountrySentinel(raw)
      name = iso ? ISO2_TO_REGION.get(iso)?.countryName : undefined
    } else {
      name = cityCountryByCity[raw]?.trim()
      if (!name) name = countryNameFromSupportedMarketCity(raw) ?? undefined
    }
    if (!name) return undefined
    countries.add(name)
  }
  if (countries.size !== 1) return undefined
  return [...countries][0]
}

export type AccordionRegion = {
  /** Stable React key */
  key: string
  countryName: string
  /** ISO2 when this block supports “whole country”; otherwise only city pills apply. */
  iso2: string | null
  cities: string[]
}

/**
 * Regions for accordion: known regions first, then leftovers grouped by Firebase country name.
 */
export function buildAccordionRegions(
  availableCities: string[],
  cityCountryByCity: Record<string, string>
): AccordionRegion[] {
  const available = new Set(availableCities)
  const used = new Set<string>()
  const regions: AccordionRegion[] = []

  for (const def of REGION_DEFINITIONS) {
    const inOrder = def.citiesInOrder.filter((c) => available.has(c))
    const extraSameCountry = availableCities.filter(
      (c) =>
        available.has(c) &&
        !def.citiesInOrder.includes(c) &&
        (cityCountryByCity[c]?.trim() ?? '') === def.countryName
    )
    extraSameCountry.sort((a, b) => a.localeCompare(b))
    const cities = [...inOrder, ...extraSameCountry]
    for (const c of cities) used.add(c)
    if (cities.length === 0) continue
    regions.push({
      key: `iso:${def.iso2}`,
      countryName: def.countryName,
      iso2: def.iso2,
      cities,
    })
  }

  const leftovers = availableCities.filter((c) => !used.has(c))
  const byCountry: Record<string, string[]> = {}
  for (const city of leftovers) {
    const label = cityCountryByCity[city]?.trim() || 'Other'
    if (!byCountry[label]) byCountry[label] = []
    byCountry[label].push(city)
  }
  const otherKeys = Object.keys(byCountry).sort((a, b) => a.localeCompare(b))
  for (const label of otherKeys) {
    const cities = [...byCountry[label]].sort((a, b) => a.localeCompare(b))
    regions.push({
      key: `extra:${label}`,
      countryName: label,
      iso2: null,
      cities,
    })
  }

  return regions
}

/** Comma-separated list for tattoo vector search (`city` field). Empty = search all embedding cities. */
export function vectorSearchCityParam(
  selected: string | undefined,
  allLocationsText: string
): string {
  const cities = resolveLocationFilterToCities(selected, allLocationsText)
  if (!cities?.length) return ''
  return cities.join(',')
}

/** Expand URL/filter tokens to launched cities only (deduped, sorted). */
export function expandLocationTokensToLaunchedCities(
  tokens: string[],
  allLocationsText: string,
  launchedCitySet: Set<string>
): string[] {
  if (!tokens.length || tokens.includes(allLocationsText)) return []
  const out = new Set<string>()
  for (const raw of tokens) {
    if (!raw || raw === allLocationsText) continue
    if (isCountrySentinel(raw)) {
      const list = resolveSentinelToCities(raw)
      if (list) {
        for (const c of list) {
          if (launchedCitySet.has(c)) out.add(c)
        }
      }
    } else {
      const resolved = resolveToLaunchedCityName(raw, launchedCitySet)
      if (resolved) out.add(resolved)
    }
  }
  return [...out].sort((a, b) => a.localeCompare(b))
}

/** Cities sent to feed / tattoo queries; `undefined` means no geographic filter. */
export function resolveSelectedLocationsToQueryCities(
  selectedLocations: string[],
  allLocationsText: string
): string[] | undefined {
  if (!selectedLocations.length || selectedLocations.includes(allLocationsText)) {
    return undefined
  }
  const out = new Set<string>()
  for (const raw of selectedLocations) {
    if (!raw || raw === allLocationsText) continue
    if (isCountrySentinel(raw)) {
      const list = resolveSentinelToCities(raw)
      if (list) list.forEach((c) => out.add(c))
    } else {
      out.add(normalizePlainCityToken(raw))
    }
  }
  if (out.size === 0) return undefined
  return [...out]
}

export function vectorSearchCityParamFromSelection(
  selectedLocations: string[],
  allLocationsText: string
): string {
  const cities = resolveSelectedLocationsToQueryCities(selectedLocations, allLocationsText)
  if (!cities?.length) return ''
  return cities.join(',')
}

/** Selection is “everywhere” when explicit all, empty, or every launched city is included. */
export function isEffectivelyAllLocations(
  selectedLocations: string[],
  allLocationsText: string,
  launchedCities: readonly string[]
): boolean {
  if (!selectedLocations.length || selectedLocations.includes(allLocationsText)) {
    return true
  }
  if (!launchedCities.length) return false
  const launched = new Set(launchedCities)
  const expanded = expandLocationTokensToLaunchedCities(
    selectedLocations,
    allLocationsText,
    launched
  )
  if (expanded.length !== launchedCities.length) return false
  return launchedCities.every((c) => expanded.includes(c))
}

export function getMultiLocationFilterDisplay(
  selectedLocations: string[],
  allLocationsText: string,
  /** When provided, a city selection that exactly matches one region’s full catalogue shows as the country name, or as the lone city name when only one city is in-market. */
  accordionRegions?: readonly AccordionRegion[]
): string {
  if (!selectedLocations.length || selectedLocations.includes(allLocationsText)) {
    return allLocationsText
  }
  const parts = selectedLocations.filter((s) => s && s !== allLocationsText)
  if (parts.length === 0) return allLocationsText

  if (accordionRegions?.length) {
    const onlyCityNames = parts.filter((p) => !isCountrySentinel(p))
    if (onlyCityNames.length === parts.length && onlyCityNames.length > 0) {
      const set = new Set(onlyCityNames)
      for (const r of accordionRegions) {
        if (!r.iso2 || r.cities.length !== set.size) continue
        if (r.cities.every((c) => set.has(c))) {
          return r.cities.length === 1 ? r.cities[0]! : r.countryName
        }
      }
    }
  }

  const labels = parts.map((s) => getLocationFilterDisplay(s, allLocationsText))
  if (labels.length === 1) return labels[0]!
  if (labels.length === 2) return `${labels[0]}, ${labels[1]}`
  return `${labels[0]} +${labels.length - 1}`
}

export function areLocationSelectionsAllLaunched(
  selectedLocations: string[],
  allLocationsText: string,
  launchedCitySet: Set<string>
): boolean {
  if (!selectedLocations.length || selectedLocations.includes(allLocationsText)) return true
  return selectedLocations.every((s) =>
    isValidLaunchedSelection(s, allLocationsText, launchedCitySet)
  )
}

/** Whether URL / deep-link selection is still offered (manual cities + sentinels with all region cities launched). */
export function isValidLaunchedSelection(
  selected: string | undefined,
  allLocationsText: string,
  launchedCitySet: Set<string>
): boolean {
  if (!selected || selected === allLocationsText) return true
  if (isCountrySentinel(selected)) {
    const list = resolveSentinelToCities(selected)
    if (!list?.length) return false
    return list.every((c) => resolveToLaunchedCityName(c, launchedCitySet) != null)
  }
  return resolveToLaunchedCityName(selected, launchedCitySet) != null
}
